import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Edit, X, Plus, Trash2 } from "lucide-react";

export default function AdminCities() {
  const { authHeaders } = useAuth();
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await api.get("/cities");
    setCities(data);
  };

  const save = async () => {
    try {
      const { slug, ...body } = editing;
      await api.put(`/cities/${slug}`, body, { headers: authHeaders() });
      toast.success("City updated!");
      setEditing(null);
      load();
    } catch {
      toast.error("Error saving");
    }
  };

  const updateSection = (idx, key, value) => {
    const sections = [...editing.content.sections];
    sections[idx] = { ...sections[idx], [key]: value };
    setEditing({ ...editing, content: { ...editing.content, sections } });
  };

  const addSection = () => {
    setEditing({
      ...editing,
      content: {
        ...editing.content,
        sections: [...editing.content.sections, { heading: "New Section", body: "" }],
      },
    });
  };

  const removeSection = (idx) => {
    const sections = editing.content.sections.filter((_, i) => i !== idx);
    setEditing({ ...editing, content: { ...editing.content, sections } });
  };

  return (
    <div data-testid="admin-cities">
      <h1 className="text-3xl font-extrabold mb-6">City Pages</h1>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">Edit: {editing.name}</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold block mb-1">Name</label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-bold block mb-1">Region</label>
                  <Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Meta Title (SEO)</label>
                <Input value={editing.meta_title} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Meta Description (SEO)</label>
                <Textarea value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Hero Title</label>
                <Input value={editing.content.hero_title} onChange={(e) => setEditing({ ...editing, content: { ...editing.content, hero_title: e.target.value } })} />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Hero Subtitle</label>
                <Textarea value={editing.content.hero_subtitle} onChange={(e) => setEditing({ ...editing, content: { ...editing.content, hero_subtitle: e.target.value } })} rows={2} />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Intro</label>
                <Textarea value={editing.content.intro} onChange={(e) => setEditing({ ...editing, content: { ...editing.content, intro: e.target.value } })} rows={4} />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="featured" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} />
                <label htmlFor="featured" className="text-sm font-bold">Featured (show in main nav)</label>
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold">Content Sections</h3>
                  <Button size="sm" variant="outline" onClick={addSection}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                <div className="space-y-4">
                  {editing.content.sections.map((s, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-4 space-y-2 relative">
                      <button onClick={() => removeSection(i)} className="absolute top-3 right-3 text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <Input value={s.heading} onChange={(e) => updateSection(i, "heading", e.target.value)} placeholder="Section heading" />
                      <Textarea value={s.body} onChange={(e) => updateSection(i, "body", e.target.value)} rows={4} placeholder="Section body" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t sticky bottom-0 bg-white">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} className="bg-brand-pink hover:bg-brand-pink-hover" data-testid="save-city">Save City</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 divide-y">
        {cities.map((c) => (
          <div key={c.slug} className="p-5 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-bold text-brand-navy">{c.name}</div>
                {c.is_featured && <span className="px-2 py-0.5 bg-pink-50 text-brand-pink text-xs font-bold rounded">Featured</span>}
              </div>
              <div className="text-xs text-slate-500">/{c.slug} • {c.region}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(c)} data-testid={`edit-city-${c.slug}`}>
              <Edit className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
